import React from 'react';
import { X, MapPin, Navigation, AlertTriangle, Truck, ExternalLink, RefreshCw } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { theme } from '../../config/theme';
import {
  getRiskScoreColor,
  formatDistance,
  formatDuration,
  formatCost,
  formatLastAnalyzed
} from '../../models/routeAnalysisModels';
import { getGoogleMapsRouteUrl } from '../../utils/geocoding';
import { Button } from '../UI/button';

const RouteDetailsDialog = ({ routeAnalysis, onClose, onAnalyze, analyzing = false }) => {
  const { t } = useLocale();
  if (!routeAnalysis || !routeAnalysis.routeId) return null;

  const riskColors = getRiskScoreColor(routeAnalysis.riskScore);
  const isStale = routeAnalysis.isStale?.() ?? false;

  const openInGoogleMaps = () => {
    const startAddress = routeAnalysis.getStartFullAddress?.() ?? '';
    const endAddress = routeAnalysis.getEndFullAddress?.() ?? '';
    window.open(getGoogleMapsRouteUrl(startAddress, endAddress), '_blank');
  };

  const startLine = [routeAnalysis.startAddress, routeAnalysis.startCity, routeAnalysis.startProvince].filter(Boolean).join(', ');
  const endLine = [routeAnalysis.endAddress, routeAnalysis.endCity, routeAnalysis.endProvince].filter(Boolean).join(', ');

  return (
    <>
      <div
        className="fixed inset-0 transition-opacity"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: theme.zIndex.overlay }}
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg max-h-[90vh] overflow-hidden rounded-xl shadow-xl flex flex-col"
        style={{ backgroundColor: colors.background.card, zIndex: theme.zIndex.modal, border: `1px solid ${colors.border.light}` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="route-details-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 px-5 py-4 border-b" style={{ borderColor: colors.border.light }}>
          <h2 id="route-details-title" className="text-lg font-semibold" style={{ color: colors.text.primary }}>
            {t('routeDetailsTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:opacity-80"
            style={{ color: colors.text.secondary }}
            aria-label={t('close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Route name + risk */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: colors.accent[100] }}
              >
                <Navigation className="h-5 w-5" style={{ color: colors.accent[600] }} />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-base truncate" style={{ color: colors.text.primary }}>
                  {routeAnalysis.routeName || `Route #${routeAnalysis.routeId}`}
                </h3>
                {routeAnalysis.description && (
                  <p className="text-sm mt-0.5 line-clamp-2" style={{ color: colors.text.secondary }}>
                    {routeAnalysis.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: riskColors.bg, color: riskColors.text }}
              >
                {routeAnalysis.riskLevel}
              </span>
              {!routeAnalysis.isActive && (
                <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: colors.gray[100], color: colors.text.secondary }}>
                  {t('routeDetailsInactive')}
                </span>
              )}
            </div>
          </div>

          {isStale && (
            <div className="flex items-center gap-2 py-2 px-3 rounded-lg text-sm" style={{ backgroundColor: colors.warning[50], color: colors.warning[700] }}>
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{t('routeDetailsAnalysisStale')}</span>
            </div>
          )}

          {/* Start → End */}
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.success[100] }}>
                <MapPin className="h-4 w-4" style={{ color: colors.success[700] }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium" style={{ color: colors.text.tertiary }}>{t('routeDetailsStartingPoint')}</p>
                <p className="text-sm font-medium mt-0.5" style={{ color: colors.text.primary }}>{startLine || '—'}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.danger[100] }}>
                <MapPin className="h-4 w-4" style={{ color: colors.danger[700] }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium" style={{ color: colors.text.tertiary }}>{t('routeDetailsDestination')}</p>
                <p className="text-sm font-medium mt-0.5" style={{ color: colors.text.primary }}>{endLine || '—'}</p>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border" style={{ borderColor: colors.border.light }}>
              <p className="text-xs font-medium mb-1" style={{ color: colors.text.secondary }}>{t('routeDetailsDistance')}</p>
              <p className="text-base font-semibold" style={{ color: colors.text.primary }}>
                {routeAnalysis.distanceText || formatDistance(routeAnalysis.distanceKm)}
              </p>
            </div>
            <div className="p-3 rounded-lg border" style={{ borderColor: colors.border.light }}>
              <p className="text-xs font-medium mb-1" style={{ color: colors.text.secondary }}>{t('routeDetailsDuration')}</p>
              <p className="text-base font-semibold" style={{ color: colors.text.primary }}>
                {routeAnalysis.durationText || formatDuration(routeAnalysis.durationMinutes)}
              </p>
              {routeAnalysis.durationInTrafficText && (
                <p className="text-xs mt-0.5" style={{ color: colors.text.tertiary }}>{routeAnalysis.durationInTrafficText} {t('routeDetailsInTraffic')}</p>
              )}
            </div>
            <div className="p-3 rounded-lg border" style={{ borderColor: colors.border.light }}>
              <p className="text-xs font-medium mb-1" style={{ color: colors.text.secondary }}>{t('routeDetailsEstCost')}</p>
              <p className="text-base font-semibold" style={{ color: colors.text.primary }}>{formatCost(routeAnalysis.estimatedCost)}</p>
            </div>
            <div className="p-3 rounded-lg border" style={{ borderColor: colors.border.light, backgroundColor: riskColors.bg || colors.gray[50] }}>
              <p className="text-xs font-medium mb-1" style={{ color: colors.text.secondary }}>{t('routeDetailsRiskScore')}</p>
              <p className="text-base font-semibold" style={{ color: riskColors.text }}>{routeAnalysis.riskScore != null ? `${Number(routeAnalysis.riskScore).toFixed(1)}/100` : '—'}</p>
            </div>
          </div>

          {/* Risk bar */}
          <div>
            <div className="flex justify-between text-xs mb-1.5">
              <span style={{ color: colors.text.secondary }}>{t('routeDetailsRiskAssessment')}</span>
              <span className="font-medium" style={{ color: riskColors.text }}>{routeAnalysis.riskLevel}</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.gray[200] }}>
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, routeAnalysis.riskScore || 0)}%`, backgroundColor: riskColors.progressBar || colors.gray[400] }}
              />
            </div>
          </div>

          {/* Risk factors */}
          {routeAnalysis.riskFactors?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2" style={{ color: colors.text.primary }}>{t('routeDetailsRiskFactors')}</h4>
              <ul className="space-y-1.5">
                {routeAnalysis.riskFactors.map((factor, i) => (
                  <li key={i} className="flex gap-2 text-sm" style={{ color: colors.text.secondary }}>
                    <span style={{ color: riskColors.text }}>•</span>
                    <span>{factor}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Transport */}
          {(routeAnalysis.carrier || routeAnalysis.vehicleType) && (
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t" style={{ borderColor: colors.border.light }}>
              {routeAnalysis.carrier && (
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4" style={{ color: colors.text.tertiary }} />
                  <span className="text-sm" style={{ color: colors.text.secondary }}>{routeAnalysis.carrier}</span>
                </div>
              )}
              {routeAnalysis.vehicleType && (
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: colors.gray[100], color: colors.text.secondary }}>
                  {routeAnalysis.vehicleType}
                </span>
              )}
            </div>
          )}

          {/* Last analyzed */}
          <p className="text-xs" style={{ color: colors.text.tertiary }}>
            {t('routeDetailsLastAnalyzed')}: {formatLastAnalyzed(routeAnalysis.lastAnalyzed)}
          </p>
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap items-center justify-end gap-2 flex-shrink-0 px-5 py-4 border-t" style={{ borderColor: colors.border.light, backgroundColor: colors.gray[50] }}>
          {onAnalyze && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAnalyze(routeAnalysis.routeId)}
              disabled={analyzing}
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${analyzing ? 'animate-spin' : ''}`} />
              {analyzing ? t('routeDetailsAnalyzing') : t('routeDetailsRefreshAnalysis')}
            </Button>
          )}
          <Button size="sm" onClick={openInGoogleMaps} style={{ backgroundColor: colors.primary[500], color: colors.text.inverse }}>
            <ExternalLink className="h-4 w-4 mr-1.5" />
            {t('routeDetailsViewInMaps')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default RouteDetailsDialog;
